import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * RateShipment — pos-delivery 1..5 stars + optional comment.
 * Hidden if shipment.status !== 'delivered' or user already rated.
 */
export default function RateShipment({ trackingCode, status }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(null);
  const [hovered, setHovered] = useState(0);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (status !== "delivered") { setLoading(false); return; }
    const fetchRating = async () => {
      const token = localStorage.getItem("blumove_token");
      if (!token) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/shipments/${trackingCode}/rating`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.rating) {
          setRating(res.data.rating);
          setStars(res.data.rating.stars);
          setComment(res.data.rating.comment || "");
          setDone(true);
        }
      } catch (e) { /* not rated yet */ }
      finally { setLoading(false); }
    };
    fetchRating();
  }, [trackingCode, status]);

  if (status !== "delivered" || loading) return null;

  const submit = async () => {
    if (stars < 1) return;
    setSubmitting(true);
    setError("");
    try {
      const token = localStorage.getItem("blumove_token");
      const res = await axios.post(`${API}/shipments/${trackingCode}/rate`,
        { stars, comment },
        { headers: { Authorization: `Bearer ${token}` } });
      setRating(res.data.rating);
      setDone(true);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || "Erro ao enviar");
    }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid={`rating-${trackingCode}`} style={{ marginTop: 14, padding: 14, borderRadius: 12, border: "1px solid var(--border-color)", background: "linear-gradient(180deg, rgba(255,193,7,0.05), rgba(255,193,7,0.0))" }}>
      <div style={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.05em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8 }}>
        {done ? t("rate.sent") : t("rate.prompt")}
      </div>
      <div className="flex items-center gap-1" onMouseLeave={() => setHovered(0)}>
        {[1,2,3,4,5].map(i => {
          const filled = (hovered || stars) >= i;
          return (
            <button
              key={i}
              data-testid={`rating-${trackingCode}-star-${i}`}
              disabled={done}
              onMouseEnter={() => !done && setHovered(i)}
              onClick={() => !done && setStars(i)}
              style={{ background: "none", border: "none", padding: 2, cursor: done ? "default" : "pointer" }}
            >
              <Star size={26} weight={filled ? "fill" : "regular"} style={{ color: filled ? "#f5a623" : "var(--border-color)", transition: "transform .15s", transform: hovered === i ? "scale(1.15)" : "scale(1)" }} />
            </button>
          );
        })}
        {stars > 0 && <span style={{ fontSize: "0.8rem", marginLeft: 8, color: "var(--text-muted)" }}>{stars}/5</span>}
      </div>
      {!done && stars > 0 && (
        <div style={{ marginTop: 10 }}>
          <textarea
            data-testid={`rating-${trackingCode}-comment`}
            value={comment}
            onChange={e => setComment(e.target.value.slice(0, 500))}
            placeholder={t("rate.comment_placeholder")}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", fontSize: "0.85rem", resize: "vertical", minHeight: 60, fontFamily: "Manrope" }}
          />
          <button
            data-testid={`rating-${trackingCode}-submit`}
            onClick={submit}
            disabled={submitting}
            style={{ marginTop: 8, padding: "8px 18px", borderRadius: 9999, border: "none", background: "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.8rem", cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? "..." : t("rate.submit")}
          </button>
          {error && <p data-testid={`rating-${trackingCode}-error`} style={{ marginTop: 6, fontSize: "0.78rem", color: "#dc2626" }}>{error}</p>}
        </div>
      )}
      {done && rating && (
        <p style={{ marginTop: 6, fontSize: "0.78rem", color: "var(--text-muted)", fontStyle: rating.comment ? "normal" : "italic" }}>
          {rating.comment || t("rate.thanks")}
        </p>
      )}
    </div>
  );
}
